import { getFancyboxButtonShareTpl } from 'components/FancyboxButtons/FancyboxButtonShare';
import Image from 'components/Image/Image';
import SharingPopup from 'components/SharingPopup';

import { trackEvent } from 'helpers/trackEvent';

import type { UnifiedCollectionItem } from '../Collection/types.d';

import type { ReactNode } from 'react';
import { renderToString } from 'react-dom/server';

interface FancyOptions {
  infobar: boolean;
  thumbs: { autoStart: boolean; axis: string };
  baseClass: string;
  buttons: string[];
  caption: string;
  share?: { tpl: string };
  btnTpl?: { share: string };
}

function buildFancyOptions(
  url: string,
  image: string,
  title: string,
  disabledImageShare: boolean,
  alwaysShare = false,
): FancyOptions {
  const showShare = alwaysShare || !disabledImageShare;
  const options: FancyOptions = {
    infobar: true,
    thumbs: { autoStart: true, axis: 'x' },
    baseClass: 'customBox',
    buttons: showShare ? ['thumbs', 'share', 'close'] : ['thumbs', 'close'],
    caption: title,
  };
  if (showShare) {
    options.share = { tpl: renderToString(<SharingPopup url={url} image={image} title={title} />) };
    options.btnTpl = { share: getFancyboxButtonShareTpl() };
  }
  return options;
}

interface FancyboxCardProps {
  className?: string;
  opacity: number;
  fancyboxKey: string;
  fancyboxOptions: FancyOptions;
  caption: string;
  href: string;
  dataType?: string;
  onClick: () => void;
  children: ReactNode;
}

function FancyboxCard({
  className,
  opacity,
  fancyboxKey,
  fancyboxOptions,
  caption,
  href,
  dataType,
  onClick,
  children,
}: FancyboxCardProps) {
  return (
    <div
      className={`collection-card${className ? ` ${className}` : ''}`}
      style={{ opacity }}
      data-fancybox={fancyboxKey}
      data-options={JSON.stringify(fancyboxOptions)}
      data-caption={caption}
      onClick={onClick}
      {...(dataType ? ({ 'data-type': dataType } as Record<string, unknown>) : {})}
      {...({ href } as Record<string, unknown>)}
    >
      {children}
    </div>
  );
}

interface LinkCardProps {
  opacity: number;
  href: string;
  onClick: () => void;
  children: ReactNode;
}

function LinkCard({ opacity, href, onClick, children }: LinkCardProps) {
  return (
    <div className="collection-card" style={{ opacity }}>
      <a href={href} target="_blank" rel="noreferrer noopener" onClick={onClick}>
        {children}
      </a>
    </div>
  );
}

export interface CollectionCardProps {
  item: UnifiedCollectionItem;
  opacity: number;
  fancyGroupKey: string;
  disabledImageShare: boolean;
}

export function CollectionCard({ item, opacity, fancyGroupKey, disabledImageShare }: CollectionCardProps) {
  if (item.type === 'photo') {
    const { image, title } = item;
    const url = item.url ?? image;
    return (
      <div
        className="collection-card collection-card--image"
        style={{ opacity }}
        onClick={() => void trackEvent('videos', 'collectionItem', image, title)}
      >
        {image.length > 0 && (
          <Image
            src={image}
            gradient
            fancybox={{
              'data-fancybox': fancyGroupKey,
              'data-options': JSON.stringify(buildFancyOptions(url, image, title, disabledImageShare)),
              'data-caption': title,
              href: image,
            }}
          />
        )}
        <h6 className="collection-card__title" dangerouslySetInnerHTML={{ __html: title }} />
      </div>
    );
  }

  if (item.type === 'video') {
    const { linkJW, thumbnail, title, url } = item;
    return (
      <FancyboxCard
        className="collection-card--video"
        opacity={opacity}
        fancyboxKey={fancyGroupKey}
        fancyboxOptions={buildFancyOptions(url, thumbnail ?? '', title, disabledImageShare, true)}
        caption={title}
        href={url}
        onClick={() => void trackEvent('collateral', 'collectionItem', url, title)}
      >
        {/* Disable Image's built-in fancybox — the card's href (video URL) must handle the click, not the thumbnail */}
        <Image
          key={linkJW}
          src={thumbnail ?? 'https://via.placeholder.com/350x250?text=Placeholder%20Thumbnail'}
          gradient
          fancybox={{ 'data-fancybox': null }}
        />
        <h6 className="collection-card__title">{title}</h6>
      </FancyboxCard>
    );
  }

  const { image, title, url, type } = item;

  if (type === 'weblink') {
    return (
      <LinkCard opacity={opacity} href={url} onClick={() => void trackEvent('collateral', url, url, title)}>
        {/* Disable Image's built-in fancybox — the anchor must handle navigation, not the thumbnail */}
        <Image key={url} src={image} gradient fancybox={{ 'data-fancybox': null }} />
        <h6 className="collection-card__title">{title}</h6>
      </LinkCard>
    );
  }

  if (type === 'iframelink' || type === 'audiolink') {
    return (
      <FancyboxCard
        opacity={opacity}
        fancyboxKey={fancyGroupKey}
        fancyboxOptions={buildFancyOptions(url, image, title, disabledImageShare)}
        caption={title}
        href={url}
        dataType="iframe"
        onClick={() => void trackEvent('collateral', url, url, title)}
      >
        {/* Disable Image's built-in fancybox — the card's href (iframe/audio URL) must handle the click, not the thumbnail */}
        <Image key={url} src={image} gradient fancybox={{ 'data-fancybox': null }} />
        <h6 className="collection-card__title">{title}</h6>
      </FancyboxCard>
    );
  }

  if (type === 'pdf') {
    const pdfUrl = `https://stack.travpromobile.com/viewer.php?file=${url}`;
    return (
      <FancyboxCard
        opacity={opacity}
        fancyboxKey={pdfUrl}
        fancyboxOptions={buildFancyOptions(pdfUrl, image, title, disabledImageShare)}
        caption={title}
        href={pdfUrl}
        onClick={() => void trackEvent('collateral', url, url, title)}
      >
        {/* Disable Image's built-in fancybox — the card's href (PDF URL) must handle the click, not the thumbnail */}
        <Image key={url} src={image} gradient fancybox={{ 'data-fancybox': null }} />
        <h6 className="collection-card__title">{title}</h6>
      </FancyboxCard>
    );
  }

  return (
    <FancyboxCard
      opacity={opacity}
      fancyboxKey={fancyGroupKey}
      fancyboxOptions={buildFancyOptions(url, image, title, disabledImageShare)}
      caption={title}
      href={url}
      onClick={() => void trackEvent('collateral', url, url, title)}
    >
      {/* Disable Image's built-in fancybox — the card's href must handle the click, not the thumbnail */}
      <Image key={url} src={image} gradient fancybox={{ 'data-fancybox': null }} />
      <h6 className="collection-card__title">{title}</h6>
    </FancyboxCard>
  );
}
